// ============================================================
// Notation — Move formatting (E:e2→A:e3 style)
// ============================================================

import { type Move, type Position, FILES } from './types';

const PLANE_CODE: Record<number, string> = { 0: 'E', 1: 'A', 2: 'V', 3: 'F' };
const PIECE_LETTER: Record<string, string> = {
  king: 'K', queen: 'Q', rook: 'R', bishop: 'B', knight: 'N', pawn: '',
};

function posToStr(p: Position): string {
  return `${FILES[p.x]}${p.y + 1}`;
}

/** Format a move in TESSERACT notation */
export function formatMove(move: Move): string {
  const plane = PLANE_CODE[move.from.w];
  const pieceLetter = PIECE_LETTER[move.piece.kind];
  const from = posToStr(move.from);
  const toPlane = PLANE_CODE[move.to.w];
  const to = posToStr(move.to);
  const capture = move.capture ? 'x' : '→';
  const check = move.isCheckmate ? '#' : move.isCheck ? '+' : '';
  const promo = move.promotion ? `=${PIECE_LETTER[move.promotion]}` : '';

  if (move.castle === 'kingside') return `${plane}:O-O${check}`;
  if (move.castle === 'queenside') return `${plane}:O-O-O${check}`;

  if (move.isCrossDimensional) {
    return `${plane}:${pieceLetter}${from}${capture}${toPlane}:${to}${promo}${check}`;
  }

  return `${plane}:${pieceLetter}${from}${capture}${to}${promo}${check}`;
}

/** Parse notation back to move components (for future use) */
export function parseNotation(notation: string): { fromPlane: string; toPlane: string; piece: string; from: string; to: string } | null {
  // Basic parser — extend as needed
  const crossMatch = notation.match(/^([EAVF]):([KQRBN]?)([a-h]\d)([→x])([EAVF]):([a-h]\d)/);
  if (crossMatch) {
    return {
      fromPlane: crossMatch[1],
      piece: crossMatch[2] || 'P',
      from: crossMatch[3],
      toPlane: crossMatch[5],
      to: crossMatch[6],
    };
  }

  const sameMatch = notation.match(/^([EAVF]):([KQRBN]?)([a-h]\d)([→x])([a-h]\d)/);
  if (sameMatch) {
    return {
      fromPlane: sameMatch[1],
      piece: sameMatch[2] || 'P',
      from: sameMatch[3],
      toPlane: sameMatch[1],
      to: sameMatch[5],
    };
  }

  return null;
}
